import SpineWrapper from '@Core/Animation/SpineWrapper';
import { EAnimationModifiers } from '@Core/Animation/SpineWrapperEventManager';
import { GameScene } from '@Core/index';
import { ReelsBackground } from '@Core/ReelsBackground/ReelsBackground';
import { Scene } from '@Core/Scene/Scene';
import { getAllEventModifiers } from '@Core/Utils/ModifierUtils';
import { AnimationState, AnimationStateData, Bone, BoundingBoxAttachment, Physics, Skeleton, Slot } from '@esotericsoftware/spine-pixi-v8';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { TPoint } from '@GFTypes/Point';
import { EOrientation } from '@Managers/Device/UseWindowDimensions.types';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Point } from 'pixi.js';

interface BoxData {
    x: number;
    y: number;
    width: number;
    height: number;
    center: { x: number; y: number };
    scaleX: number;
    scaleY: number;
}

export const VIRTUAL_SIZES = {
    PORTRAIT: { x: 1080, y: 1440 },
    LANDSCAPE: { x: 1920, y: 1080 }
};

export type TOrientationNames = keyof typeof VIRTUAL_SIZES;

export class ScaleManager {
    private readonly gameStore: GameStore;
    private readonly scalableSpines: SpineWrapper[] = [];
    private readonly spinesAttachedToReels: SpineWrapper[] = [];
    private readonly onUpdateInterval: number = 0;
    private reelsSpine: SpineWrapper;
    private reelSlotPrefix: string = ReelsBackground.REEL_SLOT_PREFIX;
    private onUpdateTimeCounter: number = 0;
    public currentOrientation: EOrientation;
    public resizedReels: boolean[] = [];

    constructor(gameStore: GameStore) {
        this.gameStore = gameStore;
        EventsCenter.on(EGameEvents.FULLSCREEN, () => this.enterFullscreen());
        EventsCenter.on(EGameEvents.EXIT_FULLSCREEN, () => this.exitFullscreen());
    }

    public onUpdate(scene: Scene, delta: number): void {
        this.onUpdateTimeCounter += delta;
        if (this.onUpdateInterval >= this.onUpdateTimeCounter) return;
        this.onUpdateTimeCounter = 0;

        if (scene instanceof GameScene && this.reelsSpine && scene.reels.reelElements && scene.reels.reelElements.length > 0) {
            this.updateReelsTransform(scene);
            this.scalableSpines
                .filter((spine) => spine.scaleToReelsWidth)
                .forEach((spine) => this.applyScaleToElement(spine, spine.fitToScreen, spine.fullScreen));
            this.spinesAttachedToReels.forEach((spine) => this.applyReelsPositionToElement(spine));

            if (this.resizedReels.length > 0 && this.resizedReels.every(Boolean)) {
                const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);
                const symbolsPerReel = scene.game.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
                const { width, height } = this.gameStore.get(EGameStoreKeys.ReelParameters);
                this.resizedReels = new Array(numberOfReels).fill(false);
                const defaultPositions = this.computeDefaultSymbolPositions(scene, width, height / symbolsPerReel);
                scene.game.gameStore.set(
                    EGameStoreKeys.ReelParameters,
                    {
                        defaultSymbolLocalPositions: defaultPositions.localPositions,
                        defaultSymbolContainerPositions: defaultPositions.containerPositions
                    },
                    true
                );

                EventsCenter.emit(EGameEvents.RESIZED_REELS);
            }
        }
    }

    public reelResized(reelIndex: number): void {
        if (this.resizedReels.length > 0) {
            this.resizedReels[reelIndex] = true;
        }
    }

    public onResize(scene: Scene): void {
        this.updateOrientation(scene);
        this.scalableSpines.forEach((spine) => this.applyScaleToElement(spine, spine.fitToScreen, spine.fullScreen));
        if (scene instanceof GameScene && this.reelsSpine) this.updateReelsTransform(scene, true);
    }

    private updateOrientation(scene: Scene) {
        const orientation = scene.screen.width > scene.screen.height ? EOrientation.LANDSCAPE : EOrientation.PORTRAIT;
        const previousOrientation = this.gameStore.get(EGameStoreKeys.CurrentOrientation);

        if (orientation === previousOrientation) return;

        this.gameStore.set(EGameStoreKeys.CurrentOrientation, orientation);
        this.gameStore.set(EGameStoreKeys.GameVirtualSize, VIRTUAL_SIZES[orientation]);
    }

    public addReelsSpine(scene: GameScene, spine: SpineWrapper): void {
        this.reelsSpine = spine;
        const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);
        this.resizedReels = new Array(numberOfReels).fill(false);
        this.addSpineToScalableMap(this.reelsSpine);
        this.updateReelsTransform(scene);
    }

    public addSpineToScalableMap(spine: SpineWrapper): void {
        this.scalableSpines.push(spine);
        this.applyScaleToElement(spine, spine.fitToScreen, spine.fullScreen);
    }

    public attachSpineToReels(spine: SpineWrapper): void {
        this.spinesAttachedToReels.push(spine);
        this.applyReelsPositionToElement(spine);
    }

    public detachSpineFromReels(spine: SpineWrapper): void {
        const index = this.spinesAttachedToReels.findIndex((_spine) => _spine === spine);
        this.spinesAttachedToReels.splice(index);
    }

    public applyReelsPositionToElement(gameObject: SpineWrapper): void {
        const scene = gameObject.scene;

        if (scene instanceof GameScene && this.reelsSpine) {
            const reelsBone = this.reelsSpine.spine.skeleton.findBone('reels');
            if (!reelsBone) throw new Error('Reels spine does not contain reels bone');
            const worldPoint = this.reelsSpine.spine.toGlobal(new Point(reelsBone.getPose().worldX, reelsBone.getPose().worldY));
            gameObject.spine.position = new Point(worldPoint.x, worldPoint.y);
        }
    }

    public applyScaleToElement(gameObject: SpineWrapper, fitToScreen: boolean = true, fullScreen: boolean = false): void {
        const scene = gameObject.scene;
        const gameVirtualSize = scene.game.gameStore.get(EGameStoreKeys.GameVirtualSize);

        const actualWidth = scene.screen.width;
        const actualHeight = scene.screen.height;

        const hasPortraitAnimations = !!gameObject.spine.state.data.skeletonData.animations.find((anim) =>
            getAllEventModifiers(anim.name).includes(EAnimationModifiers.PORTRAIT)
        );
        const isInPortrait = gameVirtualSize.x === VIRTUAL_SIZES.PORTRAIT.x;
        const missingPortraitAssets = !hasPortraitAnimations && isInPortrait && !gameObject.scaleToReelsWidth;

        const scaleX = actualWidth / (missingPortraitAssets ? VIRTUAL_SIZES.LANDSCAPE.x : gameVirtualSize.x);
        const scaleY = actualHeight / (missingPortraitAssets ? VIRTUAL_SIZES.LANDSCAPE.y : gameVirtualSize.y);

        let uniformScale;

        if (fitToScreen) {
            uniformScale = Math.min(scaleX, scaleY);
        } else {
            uniformScale = Math.max(scaleX, scaleY);
        }

        if (gameObject.scaleToReelsWidth) {
            const reelsBoneScale = this.reelsSpine.spine.skeleton.findBone(ReelsBackground.SPINE_KEY)?.getPose().scaleX ?? 1;
            uniformScale = uniformScale * reelsBoneScale;
        }

        fullScreen ? gameObject.setScale(scaleX, scaleY) : gameObject.setScale(uniformScale, uniformScale);

        if (!this.spinesAttachedToReels.includes(gameObject)) gameObject.setPosition(scene.screen.width / 2, scene.screen.height / 2);
    }

    public updateReelsTransform(scene: GameScene, onResize: boolean = false): void {
        const reelsTransform = this.computeReelsTransform();
        if (reelsTransform.length === 0) return;
        const baseReelsTransform = this.computeBaseReelsTransform();
        const reelCenters = Object.values(reelsTransform).map((reel) => reel.center);
        const symbolsPerReel = scene.game.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel);
        const previousReelParameters = scene.game.gameStore.get(EGameStoreKeys.ReelParameters);
        const previousSymbolParameters = scene.game.gameStore.get(EGameStoreKeys.SymbolParameters);
        const defaultPositions = this.computeDefaultSymbolPositions(scene, reelsTransform[0].width, reelsTransform[0].height / symbolsPerReel);
        //TODO: Migrate to PIXI? maybe normalize it?
        const hasReelSpineScaleChanged = previousReelParameters.reelsSpineScale !== this.reelsSpine.spine.scale.x;
        const hasSymbolWidthChanged = previousSymbolParameters.width !== reelsTransform[0].width;
        const hasSymbolHeightChanged = previousSymbolParameters.height !== reelsTransform[0].height / symbolsPerReel;
        const haveReelCentersChanged = !this.areVectorArraysEqual(previousReelParameters.reelCenters, reelCenters);
        const haveReelScaleChanged = !this.areVectorArraysEqual(
            previousReelParameters.scalesPerReel,
            reelsTransform.map((_) => _.scale)
        );
        const uiMarker = this.getUIMarkerPosition();
        if (hasSymbolWidthChanged || hasSymbolHeightChanged || haveReelCentersChanged || hasReelSpineScaleChanged || haveReelScaleChanged || onResize) {
            scene.game.gameStore.set(EGameStoreKeys.SymbolParameters, {
                width: reelsTransform[0].width,
                height: reelsTransform[0].height / symbolsPerReel,
                baseWidth: baseReelsTransform[0].width,
                baseHeight: baseReelsTransform[0].height / symbolsPerReel
            });

            scene.game.gameStore.set(
                EGameStoreKeys.ReelParameters,
                {
                    prevHeight: previousReelParameters.height,
                    prevWidth: previousReelParameters.width,
                    width: reelsTransform[0].width,
                    height: reelsTransform[0].height,
                    //TODO: Migrate to PIXI? maybe normalize it?
                    reelsSpineScale: this.reelsSpine?.spine.scale.x ?? 1,
                    reelCenters: reelCenters,
                    scalesPerReel: reelsTransform.map((_) => _.scale),
                    defaultSymbolLocalPositions: defaultPositions.localPositions,
                    defaultSymbolContainerPositions: defaultPositions.containerPositions,
                    uiMarker
                },
                true
            );

            EventsCenter.emit(EGameEvents.RESIZE_REELS);
            EventsCenter.emit(EGameEvents.RESIZE_SYMBOLS);
        }
    }

    private areVectorArraysEqual(a: TPoint[], b: TPoint[]): boolean {
        if (a.length !== b.length) return false;

        return a.every((vec, i) => vec.x === b[i].x && vec.y === b[i].y);
    }

    private getBoundingBoxSnapshot(skeleton: Skeleton, boxSlotNames: string[]): Record<string, BoxData> {
        const result: Record<string, BoxData> = {};

        for (const slot of skeleton.slots) {
            if (!boxSlotNames.includes(slot.data.name)) continue;
            const attachment = slot.getAppliedPose().attachment;
            if (!(attachment instanceof BoundingBoxAttachment)) continue;

            let worldVertices = new Float32Array(attachment.worldVerticesLength);
            attachment.computeWorldVertices(skeleton, slot, 0, attachment.worldVerticesLength, worldVertices, 0, 2);
            worldVertices = worldVertices.map((v) => Math.round(v)) as any;

            const xlist = worldVertices.filter((_, i) => i % 2 === 0);
            const ylist = worldVertices.filter((_, i) => i % 2 === 1);

            const minX = Math.min(...xlist),
                maxX = Math.max(...xlist);
            const minY = Math.min(...ylist),
                maxY = Math.max(...ylist);
            const width = Math.max(maxX - minX, 1);
            const height = Math.max(maxY - minY, 1);

            const appliedPose = slot.bone.getAppliedPose();

            result[slot.data.name] = {
                x: minX,
                y: minY,
                width,
                height,
                center: { x: minX + width / 2, y: minY + height / 2 },
                scaleX: appliedPose.getWorldScaleX(),
                scaleY: appliedPose.getWorldScaleY()
            };
        }

        return result;
    }

    private precomputeAllStates(skeletonData: any, boxSlotNames: string[], stateNames: string[]): Record<string, Record<string, BoxData>> {
        const skeleton = new Skeleton(skeletonData);
        const stateData = new AnimationStateData(skeletonData);
        const state = new AnimationState(stateData);

        const output: Record<string, Record<string, BoxData>> = {};

        for (const stateName of stateNames) {
            skeleton.setupPose();
            state.clearTracks();
            state.setAnimation(0, stateName, false);
            state.update(0);
            state.apply(skeleton);
            skeleton.updateWorldTransform(Physics.update);
            output[stateName] = this.getBoundingBoxSnapshot(skeleton, boxSlotNames);
        }

        return output;
    }

    private computeBaseReelsTransform(): { center: TPoint; width: number; height: number; scale: TPoint }[] {
        const reelsData: { center: TPoint; width: number; height: number; scale: TPoint }[] = [];

        const reelsSlotNames = this.reelsSpine.spine.skeleton.slots.filter((slot) => slot.data.name.startsWith(this.reelSlotPrefix));

        const animationName = this.reelsSpine.spine.skeleton.data.animations.find((animation) =>
            ['LANDSCAPE/IDLE_STATE#LOOP', 'LANDSCAPE/IDLE_STATE', 'IDLE_STATE#LANDSCAPE', 'IDLE_STATE#LANDSCAPE#LOOP'].includes(animation.name)
        )?.name!;

        const boxCache = this.precomputeAllStates(this.reelsSpine.spine.skeleton.data, [...reelsSlotNames.map((slot) => slot.data.name)], [animationName]);

        const landscapeData = boxCache[animationName];

        reelsSlotNames.forEach((slot) => {
            const reelIndex = Number(slot.data.name.replace(`${this.reelSlotPrefix}`, '')) - 1;

            const { width, height, center, scaleX, scaleY } = landscapeData[slot.data.name];

            reelsData[reelIndex] = {
                width,
                height,
                center,
                scale: new Point(scaleX, scaleY)
            };
        });

        return reelsData;
    }

    private computeReelsTransform(): { center: TPoint; width: number; height: number; scale: TPoint }[] {
        const reelsData: { center: TPoint; width: number; height: number; scale: TPoint }[] = [];

        const reelsSlotNames = this.reelsSpine.spine.skeleton.slots.filter((slot) => slot.data.name.replace(/\d+/g, '').trim() === this.reelSlotPrefix);

        reelsSlotNames.forEach((slot) => {
            const attachment = slot.getAppliedPose().getAttachment() as BoundingBoxAttachment;
            const reelIndex = Number(slot.data.name.replace(`${this.reelSlotPrefix}`, '')) - 1;

            let worldVertices: number[] = [];

            if (attachment) {
                attachment.computeWorldVertices(slot.skeleton, slot, 0, attachment.worldVerticesLength, worldVertices, 0, 2);
                worldVertices = worldVertices.map((vert) => Math.round(vert));
                const xlist = worldVertices.filter((_, index) => index % 2 === 0);
                const ylist = worldVertices.filter((_, index) => index % 2 === 1);

                const width = Math.max(Math.max(...xlist) - Math.min(...xlist), 1);
                const height = Math.max(Math.max(...ylist) - Math.min(...ylist), 1);
                const center = { x: Math.min(...xlist) + width / 2, y: Math.min(...ylist) + height / 2 };
                const scale = this.computeReelScale(slot);

                reelsData[reelIndex] = {
                    width,
                    height,
                    center,
                    scale
                };
            }
        });

        return reelsData;
    }

    private computeReelScale(slot: Slot): TPoint {
        let bone: Bone | null = slot.bone;

        let cumulativeScaleX = 1;
        let cumulativeScaleY = 1;

        while (bone) {
            cumulativeScaleX *= bone.getPose().scaleX;
            cumulativeScaleY *= bone.getPose().scaleY;

            bone = bone.parent;
        }

        const scaleX = slot.bone.getPose().getWorldScaleX();
        const scaleY = slot.bone.getPose().getWorldScaleY();

        return new Point(scaleX, scaleY);
    }

    private computeDefaultSymbolPositions(
        scene: GameScene,
        reelWidth: number,
        symbolHeight: number
    ): { localPositions: TPoint[][]; containerPositions: TPoint[][] } {
        const numberOfReels = this.gameStore.get(EGameStoreKeys.NumberOfReels);
        const numberOfVisibleSymbolsPerReel = this.gameStore.get(EGameStoreKeys.NumberOfSymbolsPerReel) + 2;
        const localPositions: TPoint[][] = [];
        const containerPositions: TPoint[][] = [];
        for (let i = 0; i < numberOfReels; i++) {
            for (let j = 0; j < numberOfVisibleSymbolsPerReel; j++) {
                if (!localPositions[i]) localPositions[i] = [];
                if (!containerPositions[i]) containerPositions[i] = [];
                localPositions[i][j] = new Point(reelWidth / 2, Math.floor(j * symbolHeight - symbolHeight / 2));
                if (!scene.reels.reelElements?.[i]) continue;
                const localPoint = new Point(localPositions[i][j].x, localPositions[i][j].y);
                const reel = scene.reels.reelElements[i];
                const globalPoint = reel.toGlobal(localPoint);
                const containerPoint = scene.reels.reelsContainer.toLocal(globalPoint);
                containerPositions[i][j] = containerPoint;
            }
        }

        return { localPositions, containerPositions };
    }

    public enterFullscreen() {
        if (!document.fullscreenElement) {
            document.documentElement
                .requestFullscreen()
                .then(() => {
                    this.gameStore.set(EGameStoreKeys.IsFullscreen, true);
                })
                .catch((err) => {
                    console.error('Failed to enter fullscreen:', err);
                });
        } else {
            this.gameStore.set(EGameStoreKeys.IsFullscreen, true);
        }
    }

    public exitFullscreen() {
        if (document.fullscreenElement) {
            document
                .exitFullscreen()
                .then(() => {
                    this.gameStore.set(EGameStoreKeys.IsFullscreen, false);
                })
                .catch((err) => {
                    console.error('Failed to exit fullscreen:', err);
                });
        } else {
            this.gameStore.set(EGameStoreKeys.IsFullscreen, false);
        }
    }

    private getUIMarkerPosition(): TPoint | undefined {
        const uiMarkerBone = this.reelsSpine.spine.skeleton.findBone('ui-marker');
        if (!uiMarkerBone) return;
        const worldPoint = this.reelsSpine.spine.toGlobal(new Point(uiMarkerBone.pose.worldX, uiMarkerBone.pose.worldY));
        return worldPoint;
    }

    public updateReelSlotPrefix(prefix: string): void {
        this.reelSlotPrefix = prefix;
    }
}
